// HuddleReport.js – fix print: remove position:absolute; flow multi‑page

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  Spinner,
  Tabs,
  TabList,
  Tab,
  useTheme,
  useColorMode,
  Divider,
  Tooltip,
  SimpleGrid,
  IconButton,
} from "@chakra-ui/react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle, faPrint } from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";
import { useReactToPrint } from "react-to-print";

function HuddleReport() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const period = new URLSearchParams(window.location.hash.slice(1)).get("period");
    return /^[0-2]$/.test(period) ? Number(period) : 1;
  });

  const { colorMode } = useColorMode();
  const theme = useTheme();
  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];
  const callCardBg = colorMode === "light" ? theme.colors.gray[100] : theme.colors.gray[800];

  // ------------ print setup -------------
  const printRef = useRef();
  const pageStyle = `@media print {
  html, body, #huddle-print {
    overflow: visible !important;
    max-height: none !important;
  }
}`;
  const handlePrint = useReactToPrint({ content: () => printRef.current, pageStyle });

  const periods = [
    { label: "Yesterday", value: 0, date: moment().subtract(1, "days") },
    { label: "Today", value: 1, date: moment() },
    { label: "Tomorrow", value: 2, date: moment().add(1, "days") },
  ];

  const transform = (item) => {
    const thirty = moment().subtract(30, "days");
    const calls = (item.call_meta || [])
      .filter((m) => moment(m.created).isAfter(thirty))
      .map((meta) => ({
        time: meta.created,
        goals: (meta.conversation_goals || []).map((g, i) => ({
          goal: g,
          score: meta.goals_score ? meta.goals_score[i] : null,
          reason: meta.goals_score_reason ? meta.goals_score_reason[i] : null,
        })),
      }));

    return {
      id: item._id,
      time: item.starttime,
      duration: item.duration || null,
      patientName: `${item.patient?.first_name ?? ""} ${item.patient?.last_name ?? ""}`.trim(),
      dob: item.patient?.dob ?? "",
      provider: `${item.provider_first_name ?? ""} ${item.provider_last_name ?? ""}`.trim(),
      operatory: item.operatory?.name ?? "",
      location_name: item.location_name,
      reason: item.initial_reason,
      calls,
    };
  };

  const fetchData = async (p = 1) => {
    setIsLoading(true);
    try {
      const resp = await cqAPI.get(`/reports/huddle?period=${p}`);
      if (resp.type === "ok") {
        const list = resp.data.map(transform).sort((a, b) => new Date(a.time) - new Date(b.time));
        setRows(list);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.location.hash = new URLSearchParams({ period: selectedPeriod }).toString();
    fetchData(selectedPeriod);
  }, [selectedPeriod]);

  if (isLoading) {
    return <Flex justifyContent="center" alignItems="center" h="100vh" w="100%"><Spinner size="xl" color="blue.500" /></Flex>;
  }

  const currentTab = periods.find((p) => p.value === selectedPeriod);

  return (
    <Box
    id="huddle-print"
    ref={printRef}
    p={4}
    overflowY="auto"
    maxHeight="100vh"
    w="100%"
  >
      <Flex justifyContent="flex-end" mb={2} className="no-print">
        <IconButton aria-label="Print" icon={<FontAwesomeIcon icon={faPrint} />} size="sm" onClick={handlePrint} />
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue" index={selectedPeriod} onChange={setSelectedPeriod} mb={4}>
        <TabList>{periods.map((p) => <Tab key={p.value}>{p.label}</Tab>)}</TabList>
      </Tabs>

      <Box>
        <Flex justifyContent="center" mb={6}><Text fontSize="lg" fontWeight="bold">{currentTab.label} {currentTab.date.format("dddd, MMMM D")}</Text></Flex>
        <Flex data-report-download-target justifyContent="flex-end" mb={2} className="no-print" />

        {rows.length === 0 ? (
          <Flex direction="column" alignItems="center" mt={10} color="gray.400"><Text fontSize="3xl" fontWeight="semibold">CLOSED</Text></Flex>
        ) : (
          <VStack align="stretch" spacing={0} divider={<Divider borderColor="gray.300" />}> {
            rows.map(r => (
              <Box key={r.id} p={4} _hover={{ bg: hoverBgColor }}>
                <Flex justifyContent="space-between" flexWrap="wrap" mb={2}><Text fontWeight="bold" fontSize="lg">{moment(r.time).format("h:mm A")}</Text><Text fontWeight="bold" fontSize="lg">{r.patientName}</Text></Flex>
                <Flex flexWrap="wrap" columnGap={6} rowGap={1} fontSize="sm" mb={2}><Text>DOB: {r.dob||"—"}</Text>{r.location_name&&<Text>Location: {r.location_name}</Text>}<Text>Provider: {r.provider||"—"}</Text><Text>Operatory: {r.operatory||"—"}</Text>{r.duration&&<Text>Duration: {r.duration} min</Text>}</Flex>
                <Text fontSize="sm" color="gray.600" mb={3}>Reason: {r.reason}</Text>
                <Text fontWeight="semibold" mb={2}>Patient Calls within the last 30 days</Text>
                {r.calls.length?(
                  <SimpleGrid columns={{base:1,sm:2,md:3,lg:4}} spacing={3}>{r.calls.map((c,i)=>(
                    <Box key={i} p={3} borderWidth="1px" borderRadius="md" bg={callCardBg}>
                      <Text fontSize="sm" fontWeight="semibold" mb={2}>{moment(c.time).format("MMM D, h:mm A")}</Text>
                      {c.goals.length?(
                        <VStack align="start" spacing={1}>{c.goals.map((g,gi)=>(<Flex key={gi} alignItems="center" fontSize="sm" wrap="wrap"><Tooltip label={g.reason||"No details"}><span><FontAwesomeIcon icon={g.score===5?faCheckCircle:faTimesCircle} style={{color:g.score===5?theme.colors.green[500]:theme.colors.red[500],marginRight:"6px"}}/></span></Tooltip><Text>{g.goal}</Text></Flex>))}</VStack>
                      ):<Text fontSize="sm" color="gray.500">No goals recorded.</Text>}
                    </Box>))}</SimpleGrid>
                ):<Text fontSize="sm" color="gray.500">No recent calls within 30 days.</Text>}
              </Box>)) }
          </VStack>
        )}
      </Box>
    </Box>
  );
}

export default HuddleReport;
